import { PolicyGetResponse } from '@lio/api-types';
import { differenceInCalendarDays } from 'date-fns';

const commonNonrenewalGrounds: string[] = [
  'Addition of prohibited or excluded operations',
  'Adverse loss history',
  'Claim activity that materially alters the risk profile',
  'Discovery of fraud related to a claim',
  'Failure to allow loss control inspections',
  'Failure to comply with underwriting conditions or warranties',
  'Failure to pay audit or adjustment premium',
  'Failure to remedy cited loss control deficiencies',
  'False or altered loss documentation',
  'Fraudulent claims activity',
  'Frequency or severity exceeding underwriting guidelines',
  'Increase in hazard beyond underwriting guidelines',
  'Insolvency, bankruptcy, or receivership',
  'Loss of required licenses or permits',
  'Loss trend indicating deteriorating risk quality',
  'Material change in operations or exposures',
  'Material Misrepresentation on the application(s)',
  'Non-payment of Premium',
  'Omission of material underwriting information',
  'Payment default on financed premium',
  'Regulatory or legal action against the insured impacting risk',
  'Relocation to an unapproved or higher-risk location',
  'Repeated late payments',
  'Sanctions or prohibited activities discovered mid-term',
  'Severe claim indicating unacceptable risk',
  'Unreported change in ownership or control',
  'Violation of safety requirements',
] as const;

const connecticutNonrenewalGroundsToDelete: string[] = [
  'Increase in hazard beyond underwriting guidelines',
  'Failure to allow loss control inspections',
  'Failure to remedy cited loss control deficiencies',
  'Violation of safety requirements',
  'Severe claim indicating unacceptable risk',
] as const;

const commonConditionalRenewalGrounds: string[] = [
  'Acceptance of revised policy terms',
  'Advance premium payment required',
  'Agreement to ongoing loss control monitoring',
  'Change in coverage form or edition',
  'Claims review meeting required prior to renewal',
  'Completion of employee safety training',
  'Completion of loss control inspection',
  'Completion of payroll, sales, or exposure audits',
  'Compliance with fire protection or security requirements',
  'Compliance with policy warranties or conditions',
  'Confirmation of operations, products, or services',
  'Confirmation of required licenses or permits',
  'Cooperation with underwriting or claims investigations',
  'Coverage restrictions or sublimits applied',
  'Deductible increase due to loss history',
  'Deductible or self-insured retention increase',
  'Execution of risk management agreements',
  'Exclusion endorsements required',
  'Fleet or driver eligibility controls',
  'Geographic or territory restrictions',
  'Implementation of claims management program',
  'Implementation of written safety program',
  'Installation of required safety equipment',
  'Reduction or elimination of specific high-risk operations',
  'Limitation of subcontractor use',
  'Loss frequency reduction required',
  'Loss severity mitigation measures required',
  'Modification of limits or attachment points',
  'Payment of outstanding audit premiums',
  'Payment plan modification',
  'Premium increase required for renewal',
  'Proof of regulatory compliance',
  'Receipt of complete and accurate renewal underwriting information',
  'Removal of optional coverages',
  'Removal of premium financing option',
  'Renewal Premium Increased by more than 15 Percent',
  'Renewal Premium Increased by more than 25 Percent',
  'Renewal Premium Increased by more than 30 Percent',
  'Resolution of billing disputes',
  'Resolution of pending legal or regulatory actions',
  'Revised contractual risk transfer practices',
  'Satisfactory remediation of prior loss control recommendations',
  'Sanctions screening clearance',
  'Signed warranties or endorsements required',
  'Submission of financial statements',
  'Submission of proof of corrective actions',
  'Updated applications or exposure schedules required',
  'Use of approved subcontractors or vendors only',
  'Use of approved vendors or risk management services',
] as const;

const getPolicyState = (policy: PolicyGetResponse) => {
  const primaryAddress = policy?.boundQuote?.quoteLocations.find((location) => location.insuredLocation.isPrimary);
  const stateFromAddress = primaryAddress?.insuredLocation.address.jurisdiction.abbreviation;
  return stateFromAddress?.toUpperCase() ?? '';
};

export const getNonrenewalGrounds = (policy: PolicyGetResponse): string[] => {
  const state = getPolicyState(policy);
  if (state === 'CT') {
    return commonNonrenewalGrounds.filter((item) => !connecticutNonrenewalGroundsToDelete.includes(item));
  }

  return [...commonNonrenewalGrounds];
};

export const getConditionalRenewalGrounds = (_policy: PolicyGetResponse): string[] => {
  return [...commonConditionalRenewalGrounds];
};

export const isLegalNoticeDaysRequirementMet = (policy: PolicyGetResponse): boolean => {
  const legalNoticeDays = Number(policy.legalNoticeDays);
  const expiresOn = policy.expiresOn;

  if (!expiresOn || Number.isNaN(legalNoticeDays) || legalNoticeDays <= 0) {
    return true;
  }

  const daysUntilExpiration = differenceInCalendarDays(new Date(expiresOn), new Date());
  return daysUntilExpiration > legalNoticeDays;
};

export const COMPLIANCE_DAYS_ERROR_MESSAGE =
  'The Number of Days notice to meet compliance has not been met. Expiration Date must be extended.';
